import {
  Box,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormGroup,
  Paper,
  Switch,
  Tooltip,
  Typography
} from "@mui/material";
import InfoIcon from '@mui/icons-material/Info';
import {
  useGetMemberInfoQuery,
  useGetUserInfoQuery,
  useUpdateMemberInfoMutation
} from '../services/api';
import { User } from '../types';
import { EditableField } from '../EditableField';

export function MemberProfile() {
  const { data: user } = useGetUserInfoQuery();
  if (user?.role !== 'member')
    return null;

  return <MemberProfileInternal
    user={user}
  />;
}

const MemberProfileInternal = ({
  user
}: {
  user?: User
}) => {
  const { data: member, isFetching, isLoading, refetch } = useGetMemberInfoQuery();
  const [updateMemberInfo, { isLoading: isLoadingUpdate }] = useUpdateMemberInfoMutation();

  return (
    <>
      {member?.type !== 'public' && member?.department && <>
        <Box
          alignItems='center'
          display='flex'
          justifyContent='space-between'
        >
          <Box
            alignItems='center'
            display='flex'
            marginRight={4}
          >
            <Typography
              fontWeight={500}
              lineHeight={1}
            >Allow Other Members from "{member?.department.name}" to Participate in Your Session?&nbsp;
              <Tooltip
                title="By selecting yes, other members from your same department may join a session that you have booked. If you
            select no, then any session you have successfully booked would be unavailable to them.">
                <InfoIcon
                  fontSize='small'
                  style={{
                    position: 'relative',
                    top: 4
                  }} />
              </Tooltip>
            </Typography>
          </Box>
          <FormGroup>
            <FormControlLabel
              control={<Switch
                checked={!member?.private}
                disabled={isLoadingUpdate || isLoading || isFetching}
                onChange={(_, checked) => {
                  console.log({ _, checked })
                  updateMemberInfo({
                    [`private`]: !checked
                  }).then(refetch)
                }}
              />}
              label={isLoadingUpdate || isLoading || isFetching
                ? <CircularProgress
                  size={25} />
                : (member?.private ? 'No' : 'Yes')}
            />
          </FormGroup>
        </Box>
        <Divider style={{
          marginBottom: 8,
          marginTop: 12,
        }}
        />
      </>
      }
      <Typography
        fontSize={22}
      >Profile Information</Typography>
      {isLoading
        ? <CircularProgress />
        : <>
          <Box
            marginY={2}
          >
            <Typography
              fontWeight={500}
              lineHeight={1}
            >Name</Typography>
            <Typography
            >{member?.fullName}</Typography>
          </Box>
          <Box
            marginY={2}
          >
            <Typography
              fontWeight={500}
              lineHeight={1}
            >Email</Typography>
            <Typography
            >{user?.email}</Typography>
          </Box>
          <Box
            marginY={2}
          >
            {member?.department && <>
              <Typography
                fontWeight={500}
                lineHeight={1}
              >Department</Typography>
              <Typography
              >{member?.department.name}</Typography>
            </>}
          </Box>
          <EditableField
            isLoading={isLoadingUpdate || isLoading || isFetching}
            label='Nickname'
            onSubmit={nickname => {
              updateMemberInfo({
                nickname,
              }).then(refetch)
            }}
            tooltip='Your nickname will appear with your video during your sessions.'
            value={member?.nickname || ''}
          />
          <Box
            marginY={2}
          >
            <Typography
              fontWeight={500}
              lineHeight={1}
            >Username</Typography>
            <Typography
            >{user?.username}</Typography>
          </Box>
          <EditableField
            isLoading={isLoadingUpdate || isLoading || isFetching}
            label='Phone'
            onSubmit={phone => {
              updateMemberInfo({
                phone,
              }).then(refetch)
            }}
            type='phone'
            value={member?.phone || ''}
          />
          {!!member?.emergencyContacts?.length && <>
            <Box
              marginY={2}
            >
              <Typography
                fontSize={22}
              >Emergency Contacts</Typography>
              {member?.emergencyContacts.map(x =>
                <Box key={x.name}>
                  <Typography>Name: {x.name}</Typography>
                  <Typography>Relationship: {x.relationship}</Typography>
                  {x.phone && <Typography>Phone: {x.phone}</Typography>}
                  {x.email && <Typography>Email: {x.email}</Typography>}
                </Box>
              )}
            </Box>
          </>}
          {!!member?.department?.emergencyResources?.length &&
            <Box
              marginY={2}
            >
              <Typography
                fontSize={22}
              >Department Emergency Resources</Typography>
              <Paper
                elevation={0}
              >
                <Box
                  bgcolor='#f3f7f9'
                  padding={2}
                >
                  {member?.department.emergencyResources.map(x => <Box key={x.name}>
                    <Typography
                      fontWeight={500}
                      lineHeight={1}
                    >{x.title}</Typography>
                    <Typography>Name: {x.name}</Typography>
                    {x.phone && <Typography>Phone: {x.phone}</Typography>}
                    {x.email && <Typography>Email: {x.email}</Typography>}
                  </Box>)}
                </Box>
              </Paper>
            </Box>
          }
        </>}
    </>);
}
